import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Loader } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  getSidePanelAuditHistoryForAccounts,
  getSidePanelAuditHistoryForEntitlements,
  getSidePanelAuditHistoryForRoles,
  getSidePanelAuditHistoryForUsers,
  getSidePanelChildForEntitlements,
  getSidePanelChildForRoles,
  getSidePanelDependentsForEntitlements,
  getSidePanelEntitlementForRoles,
  getSidePanelPrivilegesForEntitlements,
  getSidePanelSodForEntitlements,
  getSidePanelSummaryForAccounts,
  getSidePanelSummaryForEntitlements,
  getSidePanelSummaryForRoles,
  getSidePanelSummaryForUsers,
  setSidePanelLoading,
} from '../../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  REF_PROP,
} from '../../../../utils/constant';
import { SIDE_PANEL_CATEGORY_CHILD_ENT } from '../constant';

import CertsSidePanelAccounts from './CertsSidePanelAccounts';
import CertsSidePanelEntitlements from './CertsSidePanelEntitlements';
import CertsSidePanelRoles from './CertsSidePanelRoles';
import CertsSidePanelUsers from './CertsSidePanelUsers';
import {
  FORCED_CANCELLATION,
  MAX_RECORDS_TO_FETCH,
  PROMISE_FULLFILLED,
  SIDE_PANEL_CATEGORY_ACC,
  SIDE_PANEL_CATEGORY_ENT,
  SIDE_PANEL_CATEGORY_RLE,
  SIDE_PANEL_CATEGORY_USR,
  TAB_CHILDREN,
  TAB_DEPENDENT,
  TAB_ENTITLEMENT,
  TAB_HISTORY,
  TAB_PRIVILEGE,
  TAB_SOD,
  TAB_SUMMARY,
} from './constant';
import DATA from './mockData';

function CertsSidePanelBody({
  data,
  handleCloseSidePanel,
  tabGroupSidePanelRef,
  className,
  dataTestId,
}) {
  const dispatch = useDispatch();

  const {
    global: { certKey },
    core: { campaignType },
    sidePanel: {
      isSidePanelLoading,
      sidePanelAccounts,
      sidePanelEntitlements,
      sidePanelRoles,
      sidePanelUsers,
    },
  } = useSelector((state) => state?.certifications?.details);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const [activeTab, setActiveTab] = useState(TAB_SUMMARY);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [users, setUsers] = useState({
    ...data,
    sidePanel: {
      status: {
        [TAB_SUMMARY]: false,
        [TAB_HISTORY]: false,
      },
    },
  });
  const [roles, setRoles] = useState({
    ...data,
    sidePanel: {
      status: {
        [TAB_SUMMARY]: false,
        [TAB_HISTORY]: false,
        [TAB_ENTITLEMENT]: false,
        [TAB_CHILDREN]: false,
      },
    },
  });
  const [accounts, setAccounts] = useState({
    ...data,
    sidePanel: {
      unFixedTabs: isEOCampaign
        ? [TAB_SOD, TAB_PRIVILEGE, TAB_CHILDREN, TAB_DEPENDENT]
        : [],
      status: isEOCampaign
        ? {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
            [TAB_SOD]: false,
            [TAB_PRIVILEGE]: false,
            [TAB_CHILDREN]: false,
            [TAB_DEPENDENT]: false,
          }
        : {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
          },
    },
  });
  const [entitlements, setEntitlements] = useState({
    ...data,
    sidePanel: {
      unFixedTabs: isEOCampaign
        ? [TAB_CHILDREN, TAB_DEPENDENT]
        : [TAB_SOD, TAB_PRIVILEGE, TAB_CHILDREN, TAB_DEPENDENT],
      status: isEOCampaign
        ? {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
            [TAB_CHILDREN]: false,
            [TAB_DEPENDENT]: false,
          }
        : {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
            [TAB_SOD]: false,
            [TAB_PRIVILEGE]: false,
            [TAB_CHILDREN]: false,
            [TAB_DEPENDENT]: false,
          },
    },
  });
  const promisesForSidePanel = [];

  const sidePanelBodyClasses = classnames(
    'SidePanelView',
    (isSidePanelLoading || isLoadingHistory) &&
      'CertsSidePanelBody--isSidePanelLoading',
    className
  );

  const callApiForHistory = () => {
    setIsLoadingHistory(true);

    switch (data?.type) {
      case SIDE_PANEL_CATEGORY_USR:
        promisesForSidePanel.push(
          dispatch(
            getSidePanelAuditHistoryForUsers(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            const statusToExtend = {};

            responses.forEach((response) => {
              if (response.status === PROMISE_FULLFILLED) {
                statusToExtend[response.value] = true;
              }
            });

            setUsers((prev) => ({
              ...prev,
              sidePanel: {
                ...prev.sidePanel,
                status: { ...prev.sidePanel.status, ...statusToExtend },
              },
            }));
          })
          .finally(() => {
            setIsLoadingHistory(false);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_RLE:
        promisesForSidePanel.push(
          dispatch(
            getSidePanelAuditHistoryForRoles(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ROLEKEY
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            const statusToExtend = {};

            responses.forEach((response) => {
              if (response.status === PROMISE_FULLFILLED) {
                statusToExtend[response.value] = true;
              }
            });

            setRoles((prev) => ({
              ...prev,
              sidePanel: {
                ...prev.sidePanel,
                status: { ...prev.sidePanel.status, ...statusToExtend },
              },
            }));
          })
          .finally(() => {
            setIsLoadingHistory(false);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_ACC:
        promisesForSidePanel.push(
          dispatch(
            getSidePanelAuditHistoryForAccounts(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            const statusToExtend = {};

            responses.forEach((response) => {
              if (response.status === PROMISE_FULLFILLED) {
                statusToExtend[response.value] = true;
              }
            });

            setAccounts((prev) => ({
              ...prev,
              sidePanel: {
                ...prev.sidePanel,
                status: { ...prev.sidePanel.status, ...statusToExtend },
              },
            }));
          })
          .finally(() => {
            setIsLoadingHistory(false);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_ENT:
      case SIDE_PANEL_CATEGORY_CHILD_ENT:
        promisesForSidePanel.push(
          dispatch(
            getSidePanelAuditHistoryForEntitlements(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY,
              data.CERT_ROLEKEY,
              data?.CERT_ENTITLEMENT_VALUE2KEY?.toString()
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            const statusToExtend = {};

            responses.forEach((response) => {
              if (response.status === PROMISE_FULLFILLED) {
                statusToExtend[response.value] = true;
              }
            });

            setEntitlements((prev) => ({
              ...prev,
              sidePanel: {
                ...prev.sidePanel,
                status: { ...prev.sidePanel.status, ...statusToExtend },
              },
            }));
          })
          .finally(() => {
            setIsLoadingHistory(false);
            promisesForSidePanel.length = 0;
          });
        break;

      default:
        break;
    }
  };

  const checksInThenBlock = (responses, setterFunc) => {
    const isAtleastOnePromiseFullfiled = responses.some(
      (response) => response.status === PROMISE_FULLFILLED
    );

    const isAtleastOnePromiseForcedCancelled = responses.some(
      (response) => response?.reason?.message === FORCED_CANCELLATION
    );

    if (isAtleastOnePromiseForcedCancelled) return;

    if (!isAtleastOnePromiseFullfiled) {
      throw new Error({ status: false });
    }

    const statusToExtend = {};

    responses.forEach((response) => {
      if (response.status === PROMISE_FULLFILLED) {
        statusToExtend[response.value] = true;
      }
    });

    setterFunc((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        status: { ...prev.sidePanel.status, ...statusToExtend },
      },
    }));
    dispatch(setSidePanelLoading(false));
  };

  const checksInCatchBlock = (state, setterFunc) => {
    const statusToExtend = {};

    Object.keys(state?.sidePanel.status).forEach((key) => {
      statusToExtend[key] = false;
    });

    setterFunc((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        status: { ...prev.sidePanel.status, ...statusToExtend },
      },
    }));
    dispatch(setSidePanelLoading(false));
  };

  const callApiByType = () => {
    switch (data?.type) {
      case SIDE_PANEL_CATEGORY_USR:
        dispatch(setSidePanelLoading(true));
        promisesForSidePanel.push(
          dispatch(
            getSidePanelSummaryForUsers(data.id, certKey, data.CERT_USERKEY)
          ),
          dispatch(
            getSidePanelAuditHistoryForUsers(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            checksInThenBlock(responses, setUsers);
          })
          .catch(() => {
            checksInCatchBlock(users, setUsers);
          })
          .finally(() => {
            setActiveTab(TAB_SUMMARY);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_RLE:
        dispatch(setSidePanelLoading(true));
        promisesForSidePanel.push(
          dispatch(
            getSidePanelSummaryForRoles(data.id, certKey, data.CERT_ROLEKEY)
          ),
          dispatch(
            getSidePanelAuditHistoryForRoles(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ROLEKEY
            )
          ),
          dispatch(
            getSidePanelEntitlementForRoles(
              data.CERT_ROLEKEY,
              '0',
              String(MAX_RECORDS_TO_FETCH)
            )
          ),
          dispatch(
            getSidePanelChildForRoles(
              data.CERT_ROLEKEY,
              '0',
              String(MAX_RECORDS_TO_FETCH)
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            checksInThenBlock(responses, setRoles);
          })
          .catch(() => {
            checksInCatchBlock(roles, setRoles);
          })
          .finally(() => {
            setActiveTab(TAB_SUMMARY);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_ACC:
        dispatch(setSidePanelLoading(true));
        promisesForSidePanel.push(
          dispatch(getSidePanelSummaryForAccounts(data.id, certKey)),
          dispatch(
            getSidePanelAuditHistoryForAccounts(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY
            )
          )
        );

        // for EO Campaign
        if (isEOCampaign) {
          promisesForSidePanel.push(
            dispatch(
              getSidePanelPrivilegesForEntitlements(
                data.CERT_ENTITLEMENT_VALUEKEY,
                data.CERT_ACCOUNTKEY,
                '0',
                String(MAX_RECORDS_TO_FETCH)
              )
            ),
            dispatch(
              getSidePanelSodForEntitlements(
                certKey,
                data.CERT_ENTITLEMENT_VALUEKEY,
                data.CERT_ACCOUNTKEY,
                '0',
                String(MAX_RECORDS_TO_FETCH),
                ''
              )
            )
          );
        }

        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            checksInThenBlock(responses, setAccounts);
          })
          .catch(() => {
            checksInCatchBlock(accounts, setAccounts);
          })
          .finally(() => {
            setActiveTab(TAB_SUMMARY);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_ENT:
        dispatch(setSidePanelLoading(true));
        promisesForSidePanel.push(
          dispatch(
            getSidePanelSummaryForEntitlements(
              data.id,
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data?.CERT_ENTITLEMENT_VALUE2KEY
            )
          ),
          dispatch(
            getSidePanelAuditHistoryForEntitlements(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY,
              data.CERT_ROLEKEY,
              data?.CERT_ENTITLEMENT_VALUE2KEY?.toString()
            )
          ),
          dispatch(
            getSidePanelDependentsForEntitlements(
              data.CERT_ENTITLEMENT_VALUEKEY,
              '0',
              String(MAX_RECORDS_TO_FETCH)
            )
          )
        );

        if (!isEOCampaign) {
          promisesForSidePanel.push(
            dispatch(
              getSidePanelPrivilegesForEntitlements(
                data.CERT_ENTITLEMENT_VALUEKEY,
                data.CERT_ACCOUNTKEY,
                '0',
                String(MAX_RECORDS_TO_FETCH)
              )
            ),
            dispatch(
              getSidePanelSodForEntitlements(
                certKey,
                data.CERT_ENTITLEMENT_VALUEKEY,
                data.CERT_ACCOUNTKEY,
                '0',
                String(MAX_RECORDS_TO_FETCH),
                ''
              )
            ),
            dispatch(
              getSidePanelChildForEntitlements(
                data.CERT_ENTITLEMENT_VALUEKEY,
                '0',
                String(MAX_RECORDS_TO_FETCH)
              )
            )
          );
        }

        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            checksInThenBlock(responses, setEntitlements);
          })
          .catch(() => {
            checksInCatchBlock(entitlements, setEntitlements);
          })
          .finally(() => {
            setActiveTab(TAB_SUMMARY);
            promisesForSidePanel.length = 0;
          });
        break;

      case SIDE_PANEL_CATEGORY_CHILD_ENT:
        dispatch(setSidePanelLoading(true));
        promisesForSidePanel.push(
          dispatch(
            getSidePanelSummaryForEntitlements(
              data.id,
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data?.CERT_ENTITLEMENT_VALUE2KEY
            )
          ),
          dispatch(
            getSidePanelAuditHistoryForEntitlements(
              certKey,
              data.CERT_ENTITLEMENT_VALUEKEY,
              data.CERT_USERKEY,
              data.CERT_ACCOUNTKEY,
              data.CERT_ROLEKEY,
              data?.CERT_ENTITLEMENT_VALUE2KEY?.toString()
            )
          ),
          dispatch(
            getSidePanelDependentsForEntitlements(
              data?.CERT_ENTITLEMENT_VALUE2KEY?.toString(),
              '0',
              String(MAX_RECORDS_TO_FETCH)
            )
          )
        );
        Promise.allSettled(promisesForSidePanel)
          .then((responses) => {
            checksInThenBlock(responses, setEntitlements);
          })
          .catch(() => {
            checksInCatchBlock(entitlements, setEntitlements);
          })
          .finally(() => {
            setActiveTab(TAB_SUMMARY);
            promisesForSidePanel.length = 0;
          });
        break;

      default:
        break;
    }
  };

  const sidePanelBodyRef = useRef(null);

  // Remove scrollable tab panels from tab order (Chrome adds implicit focusability to overflow:auto elements)
  useEffect(() => {
    if (sidePanelBodyRef.current) {
      const tabPanels =
        sidePanelBodyRef.current.querySelectorAll('[role="tabpanel"]');

      tabPanels.forEach((panel) => {
        panel.setAttribute('tabindex', '-1');
      });
    }
  }, []);

  useEffect(() => {
    setUsers((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        ...sidePanelUsers,
      },
    }));
  }, [sidePanelUsers]);

  useEffect(() => {
    setRoles((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        ...sidePanelRoles,
      },
    }));
  }, [sidePanelRoles]);

  useEffect(() => {
    setAccounts((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        ...sidePanelAccounts,
      },
    }));
  }, [sidePanelAccounts]);

  useEffect(() => {
    setEntitlements((prev) => ({
      ...prev,
      sidePanel: {
        ...prev.sidePanel,
        ...sidePanelEntitlements,
      },
    }));

    if (isEOCampaign) {
      setAccounts((prev) => ({
        ...prev,
        sidePanel: {
          ...prev.sidePanel,
          ...sidePanelEntitlements,
          ...sidePanelAccounts,
        },
      }));
    }
  }, [sidePanelEntitlements]);

  useEffect(() => {
    if (data && certKey) {
      dispatch(setSidePanelLoading(true));
      setActiveTab(TAB_SUMMARY);

      setUsers({
        ...data,
        sidePanel: {
          status: {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
          },
        },
      });

      setRoles({
        ...data,
        sidePanel: {
          status: {
            [TAB_SUMMARY]: false,
            [TAB_HISTORY]: false,
            [TAB_ENTITLEMENT]: false,
            [TAB_CHILDREN]: false,
          },
        },
      });

      if (isEOCampaign) {
        setAccounts({
          ...data,
          sidePanel: {
            unFixedTabs: [TAB_SOD, TAB_PRIVILEGE, TAB_CHILDREN, TAB_DEPENDENT],
            status: {
              [TAB_SUMMARY]: false,
              [TAB_HISTORY]: false,
              [TAB_SOD]: false,
              [TAB_PRIVILEGE]: false,
              [TAB_CHILDREN]: false,
              [TAB_DEPENDENT]: false,
            },
          },
        });
      } else {
        setAccounts({
          ...data,
          sidePanel: {
            status: {
              [TAB_SUMMARY]: false,
              [TAB_HISTORY]: false,
            },
          },
        });
      }

      setEntitlements({
        ...data,
        sidePanel: {
          unFixedTabs:
            isEOCampaign &&
            (data?.type === SIDE_PANEL_CATEGORY_CHILD_ENT ||
              data?.type === SIDE_PANEL_CATEGORY_ENT)
              ? [TAB_SOD, TAB_PRIVILEGE, TAB_DEPENDENT]
              : [TAB_SOD, TAB_PRIVILEGE, TAB_CHILDREN, TAB_DEPENDENT],
          status:
            isEOCampaign &&
            (data?.type === SIDE_PANEL_CATEGORY_CHILD_ENT ||
              data?.type === SIDE_PANEL_CATEGORY_ENT)
              ? {
                  [TAB_SUMMARY]: false,
                  [TAB_HISTORY]: false,
                  [TAB_SOD]: false,
                  [TAB_PRIVILEGE]: false,
                  [TAB_DEPENDENT]: false,
                }
              : {
                  [TAB_SUMMARY]: false,
                  [TAB_HISTORY]: false,
                  [TAB_SOD]: false,
                  [TAB_PRIVILEGE]: false,
                  [TAB_CHILDREN]: false,
                  [TAB_DEPENDENT]: false,
                },
        },
      });

      callApiByType();
    }
  }, [certKey, data]);

  useEffect(() => {
    if (activeTab === TAB_HISTORY) {
      callApiForHistory();
    }
  }, [activeTab]);

  const retriggerApisForError = () => {
    dispatch(setSidePanelLoading(true));
    callApiByType();
  };

  const renderSidePanelView = () => {
    switch (data?.type) {
      case SIDE_PANEL_CATEGORY_USR:
        return (
          <CertsSidePanelUsers
            tabGroupSidePanelRef={tabGroupSidePanelRef}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            data={users}
            isLoadingHistory={isLoadingHistory}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
          />
        );

      case SIDE_PANEL_CATEGORY_RLE:
        return (
          <CertsSidePanelRoles
            tabGroupSidePanelRef={tabGroupSidePanelRef}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            data={roles}
            isLoadingHistory={isLoadingHistory}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
          />
        );

      case SIDE_PANEL_CATEGORY_ACC:
        return (
          <CertsSidePanelAccounts
            tabGroupSidePanelRef={tabGroupSidePanelRef}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            data={accounts}
            isLoadingHistory={isLoadingHistory}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
          />
        );
      case SIDE_PANEL_CATEGORY_CHILD_ENT:
      case SIDE_PANEL_CATEGORY_ENT:
        return (
          <CertsSidePanelEntitlements
            tabGroupSidePanelRef={tabGroupSidePanelRef}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            data={entitlements}
            isLoadingHistory={isLoadingHistory}
            retriggerApisForError={retriggerApisForError}
            handleCloseSidePanel={handleCloseSidePanel}
          />
        );

      default:
        return null;
    }
  };

  return (
    <aside
      ref={sidePanelBodyRef}
      className={sidePanelBodyClasses}
      data-test-id={dataTestId}>
      {isSidePanelLoading ? (
        <Box className='SidePanelView-loader'>
          <Loader kind='dots' format='inline' color='info' size='medium' />
        </Box>
      ) : (
        renderSidePanelView()
      )}
    </aside>
  );
}

CertsSidePanelBody.propTypes = {
  data: PropTypes.shape(DATA).isRequired,
  handleCloseSidePanel: PropTypes.func.isRequired,
  tabGroupSidePanelRef: REF_PROP.isRequired,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
};

CertsSidePanelBody.defaultProps = {
  className: null,
  dataTestId: null,
};

export default CertsSidePanelBody;
